import { createSelector } from '@reduxjs/toolkit';
import type { IReviewsItem } from 'src/components/Reviews/ReviewsItem/ReviewsItem';
import type { FiltersProps } from '@lt/api/dist/methods/hotelReviews/types';
import { getHotelLink } from 'src/routes/Hotel/store/hotel';
import { REVIEWS_PATH_SUFFIX } from '../../routes/Hotel/constants/reviews';

import {
  getFromImmutableStateCompatible,
  getImmutableCompatible,
} from '../../utils/immutableUtils';
import type { ReviewStore } from './types';

const _getReviewsState = (state) => getImmutableCompatible(state, 'reviews');
export const getReviewsState =
  getFromImmutableStateCompatible<ReviewStore>(_getReviewsState);

export const getStatusReviews = createSelector(
  getReviewsState,
  (reviews) => reviews.status,
);

export const getIsReviewsUpgrading = createSelector(
  getStatusReviews,
  (status) => status === 'upgrading',
);

export const getIsNewLoading = createSelector(
  getStatusReviews,
  (status) => status === 'loading',
);

export const getErrorResult = createSelector(
  getStatusReviews,
  (status) => status === 'error',
);

export const getReviewsVisible = createSelector(
  getReviewsState,
  (reviews) => reviews.visibleCount,
);

export const getReviews = createSelector(
  getReviewsState,
  (state) => state.reviews,
);
export const getReviewsLTRaw = createSelector(
  getReviewsState,
  (reviews) => reviews.data,
);

export const getPaginationParams = createSelector(
  getReviewsState,
  (reviews) => reviews.pagination,
);
export const getReviewsTotal = createSelector(
  getPaginationParams,
  (pagination) => pagination.total,
);

export const getReviewsPage = createSelector(
  getPaginationParams,
  (pagination) => pagination.page,
);

export const getSorting = createSelector(
  getReviewsState,
  (reviews) => reviews.sorting,
);

export const getFiltersReviews = createSelector(
  getReviewsState,
  (reviews) => reviews.filters,
);

export const getFlattenFilters = createSelector(getFiltersReviews, (filters) =>
  Object.values(filters),
);

export const getFiltersSelected = createSelector(
  getFlattenFilters,
  (flattenFilters) =>
    flattenFilters
      .filter((filter) => filter.isSelected)
      .map((selectedFilters) => selectedFilters.name)
      .join(),
);

export const getFiltersByName = (name: string) =>
  createSelector(getFiltersReviews, (filters): FiltersProps => filters[name]);

export const getReviewsLT = createSelector(
  getReviewsLTRaw,
  (items): IReviewsItem[] =>
    items
      ? items.map((item) =>
          // старые компоненты используют старые поля
          ({
            ...item,
            author_image: item.avatar_url ?? '',
            initials: item.name,
            phrase: item.title ?? '',
          }),
        )
      : [],
);

export const getIsShowMoreReviews = createSelector(
  [getReviewsTotal, getReviewsVisible],
  (total, visibleCount) => total > visibleCount,
);

export const getReviewsLTVisible = createSelector(
  [getReviewsLT, getReviewsVisible],
  (items, visible) => items.slice(0, visible),
);
export const getReviewsLTVisibleRaw = createSelector(
  [getReviewsLTRaw, getReviewsVisible],
  (items, visible) => items?.slice(0, visible),
);
export const getReviewsLTShowMoreVisible = createSelector(
  [getReviewsLT, getReviewsVisible],
  (items, visible) => items.length > visible,
);

export const getLastReview = createSelector(
  getReviewsLT,
  (items) => items.length > 0 && items[0],
);

export const isLastReview = createSelector([getLastReview], (lastReview) =>
  Boolean(lastReview),
);

export const getReviewsCount = createSelector(
  getReviewsLT,
  (reviews) => reviews.length,
);

/**
 * Возвращает ссылку на отельные отзывы
 * @param {object} state reducer
 * @returns hotel public_url(stirng) + '/reviews'
 */
export const getHotelReviewsLink = (state) =>
  `${getHotelLink(state)}${REVIEWS_PATH_SUFFIX}`;

export const getHasReviewsSection = createSelector(
  [getReviewsCount, getReviewsTotal],
  (count, total) => count > 0 || total > 0,
);

export const getIsNewVariant = createSelector(
  [getReviewsState],
  (reviews) => reviews.variant === 'new',
);
